import React, { useEffect, useState } from "react";
import { Button, Form, Modal, Spinner } from "react-bootstrap";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEdit, faSpinner } from "@fortawesome/free-solid-svg-icons";
import { CartPersonData } from "../..";
import { Col, Input, Label, Row } from "reactstrap";
import {
  useLazyGetCartPersonDetailsByIdQuery,
  useUpdateCartPersonDetailsMutation,
  useUpdateCartPersonImageMutation,
  useUploadCartPersonImageFileExtntionHttpsMutation,
} from "../../../../redux/services/cartSearchService";
import { formatTimestamp } from "../../../../utility/Utility";
import EditPhotoUpload from "../../../../components/EditPhotoUpload";
import { uploadImage } from "../../../../components/imageUploadCompont";
import ButtonComponentV2 from "../../../../components/ButtonComponentV2";
import EditButtonComponent from "../../../../components/EditButtonComponent";

export interface CartPersonDetailsProps {
  cartPerson: CartPersonData | undefined;
}

const CartPersonDetails: React.FC<CartPersonDetailsProps> = ({
  cartPerson,
}) => {
  const navigate = useNavigate();

  const [isSubmitLoading, setIsSubmitLoading] = useState(false);
  const [showImageUpdateModal, setShowImageUpdateModal] = useState(false);
  const [isSaveButtonDisabled, setIsSaveButtonDisabled] = useState(true);
  const [isImageUpdateLoading, setIsImageUpdateLoading] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);

  const [uploadedImgDetails, setUploadedImgDetails] = useState<{
    imgSrc: string;
    formData: any;
    fileExtention: string;
  }>({
    imgSrc: "",
    formData: "",
    fileExtention: "",
  });

  // Fetch cart person data using RTK query
  const [trigger, { data: cartPersonData, isLoading }] =
    useLazyGetCartPersonDetailsByIdQuery();

  // State for editable cart person details
  const [cartPersonDetails, setCartPersonDetails] = useState({
    cartPersonId: cartPerson?.id,
    displayName: cartPerson?.displayName || "",
    email: cartPerson?.email || "",
    phoneNumber: cartPerson?.phoneNumber || "",
    merchantId: cartPerson?.merchantId || "",
    canSelfRegisterCarts: cartPerson?.canSelfRegisterCarts === true,
    canCollectCash: cartPerson?.canCollectCash === true, // Added canCollectCash
  });

  // Update cartPersonDetails when API data is fetched
  useEffect(() => {
    if (cartPerson?.id) {
      trigger(cartPerson.id);
    }
    if (cartPersonData) {
      setCartPersonDetails({
        cartPersonId: cartPersonData.id,
        displayName: cartPersonData.displayName || "",
        email: cartPersonData.email || "",
        phoneNumber: cartPersonData.phoneNumber || "",
        merchantId: cartPersonData.merchantId || "",
        canSelfRegisterCarts: cartPersonData.canSelfRegisterCarts || false,
        canCollectCash: cartPersonData.canCollectCash || false, // Added canCollectCash
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cartPersonData]);

  // Mutation for updating cart person details
  const [updateCartPersonDetails] = useUpdateCartPersonDetailsMutation();

  // Handle input changes
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setCartPersonDetails((prevDetails) => ({
      ...prevDetails,
      [name]: value,
    }));
  };

  // Handle checkbox changes
  const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, checked } = e.target;
    setCartPersonDetails((prevDetails) => ({
      ...prevDetails,
      [name]: checked,
    }));
  };

  // Handle form submission
  const handleUpdateCartPersonData = async () => {
    try {
      setIsSubmitLoading(true);
      await updateCartPersonDetails(cartPersonDetails).unwrap();
      toast.success("Successfully updated cart person details");
      navigate("/cartpersons/cartperson-list");
      setIsSubmitLoading(false);
    } catch (error: any) {
      if (error.data === "This merchant Id Already Assigned to a Cart Person") {
        toast.error("This merchant Id Already Assigned to a Cart Person");
        setIsSubmitLoading(false);
        return;
      }
      if (error.originalStatus === 409) {
        toast.error("This merchant Id Already Assigned to a Cart Person");
        setIsSubmitLoading(false);
      } else {
        toast.error("Could not create cart");
        setIsSubmitLoading(false);
      }
      setIsSubmitLoading(false);
    }
  };

  const handleUpdatePhoto = () => {
    setShowImageUpdateModal(true);
  };

  const imageUploadCallback = (e: any) => {
    setUploadedImgDetails({
      ...uploadedImgDetails,
      imgSrc: e.imgSrc,
      formData: e.formData,
      fileExtention: e.fileExtention,
    });
    setIsSaveButtonDisabled(false);
  };

  const handleCloseModal = () => {
    setShowImageUpdateModal(false);
    setIsSaveButtonDisabled(true);
    setUploadedImgDetails({ imgSrc: "", formData: "", fileExtention: "" });
  };
  const [getCartPersonImageReference] =
    useUploadCartPersonImageFileExtntionHttpsMutation();

  const [updateCartPersonImage] = useUpdateCartPersonImageMutation();

  const handleCartPersonImageUpdate = async () => {
    setIsImageUpdateLoading(true);
    const imageRef = await uploadImage(getCartPersonImageReference, {
      fileExtention: uploadedImgDetails.fileExtention,
      formData: uploadedImgDetails.formData,
    });
    const updateImageResponse = await updateCartPersonImage({
      imageRef: imageRef,
      cartPersonId: cartPerson?.id,
    });
    if ("data" in updateImageResponse) {
      toast.success("Cart Image Successfully Updated");
      navigate("/cartpersons/cartperson-list");
    } else {
      toast.error("Something Went Wrong");
    }
    setIsImageUpdateLoading(false);
  };

  const handleEdit = () => {
    setIsEditMode(!isEditMode);
  }

  return (
    <>
      {isLoading ? (
        <div
          className="d-flex justify-content-center align-items-center"
          style={{ height: "200px" }}
        >
          <Spinner animation="border" variant="primary" role="status">
            <span className="visually-hidden">Loading...</span>
          </Spinner>
          <span className="ms-2">Please Wait</span>
        </div>
      ) : (
        <>
          <Form>
            <div className="d-flex justify-content-end"><EditButtonComponent onClick={handleEdit} /></div>
            <Row className="mb-1">
              <Col lg="4" className="cart-search-label-col">
                <Label>Profile Picture</Label>
              </Col>
              <Col lg="8">
                <img
                  src={cartPerson?.photoUrl}
                  alt="cartimage"
                  style={{
                    width: "100px",
                    height: "100px",
                    borderRadius: "300px",
                  }}
                />
                <Button
                  onClick={handleUpdatePhoto}
                  variant="danger"
                  size="sm"
                  style={{ position: "relative", top: "45%", right: "5%" }}
                  title="Edit Photo"
                  disabled={!isEditMode}
                >
                  <FontAwesomeIcon icon={faEdit} />
                </Button>
              </Col>
            </Row>
            <hr />
            <Row className="mb-1">
              <Col lg="4" className="cart-search-label-col">
                <Label>Name</Label>
              </Col>
              <Col lg="6">
                <Input
                  name="displayName"
                  value={cartPersonDetails.displayName}
                  onChange={handleInputChange}
                  disabled={!isEditMode}
                />
              </Col>
            </Row>
            <hr />
            <Row className="mb-1">
              <Col lg="4" className="cart-search-label-col">
                <Label>Email</Label>
              </Col>
              <Col lg="6">
                <Input
                  name="email"
                  value={cartPersonDetails.email}
                  onChange={handleInputChange}
                  disabled={true}
                />
              </Col>
            </Row>
            <hr />
            <Row className="mb-1">
              <Col lg="4" className="cart-search-label-col">
                <Label>Mobile Number</Label>
              </Col>
              <Col lg="6">
                <Input
                  name="phoneNumber"
                  value={cartPersonDetails.phoneNumber}
                  onChange={handleInputChange}
                  disabled={!isEditMode}
                />
              </Col>
            </Row>
            <hr />
            <Row className="mb-1">
              <Col lg="4" className="cart-search-label-col">
                <Label>Merchant Id</Label>
              </Col>
              <Col lg="6">
                <Input
                  name="merchantId"
                  value={cartPersonDetails.merchantId}
                  onChange={handleInputChange}
                  disabled={!isEditMode}
                />
              </Col>
            </Row>
            <hr />
            <Row>
              <Col lg="4">
                <Label
                  className="cart-search-label-col"
                  htmlFor="canSelfRegisterCarts"
                >
                  Can Self Register Carts
                </Label>
              </Col>
              <Col lg="8">
                <input
                  type="checkbox"
                  className="form-check-input"
                  id="canSelfRegisterCarts"
                  name="canSelfRegisterCarts"
                  checked={cartPersonDetails.canSelfRegisterCarts}
                  onChange={handleCheckboxChange}
                  disabled={!isEditMode}
                />
              </Col>
            </Row>
            <hr />
            {/* Added Can Collect Cash Checkbox */}
            <Row>
              <Col lg="4">
                <Label
                  className="cart-search-label-col"
                  htmlFor="canCollectCash"
                >
                  Can Collect Cash
                </Label>
              </Col>
              <Col lg="8">
                <input
                  type="checkbox"
                  className="form-check-input"
                  id="canCollectCash"
                  name="canCollectCash"
                  checked={cartPersonDetails.canCollectCash}
                  onChange={handleCheckboxChange}
                  disabled={!isEditMode}
                />
              </Col>
            </Row>
            <hr />
            <Row className="mb-3">
              <Col lg="4" className="cart-search-label-col">
                <Label>Created At</Label>
              </Col>
              <Col lg="8">{formatTimestamp(cartPersonData?.createdAt)}</Col>
            </Row>
            <hr />
            <div className="d-flex justify-content-end">
              {isSubmitLoading ? (
                <ButtonComponentV2
                  titel="Saving..."
                  icon={<FontAwesomeIcon icon={faSpinner} spin />}
                  isDisabled={true}
                  color="#fff"
                  backgroundColor="#590B21"
                />
              ) : (
                <ButtonComponentV2
                  titel="Save"
                  onClick={handleUpdateCartPersonData}
                  color="#fff"
                  backgroundColor="#590B21"
                  isDisabled={!isEditMode}
                />
              )}
            </div>
          </Form>
          <Modal
            show={showImageUpdateModal}
            onHide={handleCloseModal}
            centered
            backdrop="static"
          >
            <Modal.Header closeButton>
              <Modal.Title>Update Profile Photo</Modal.Title>
            </Modal.Header>
            <Modal.Body>
              <EditPhotoUpload
                imageUrl={uploadedImgDetails.imgSrc}
                onUpload={imageUploadCallback}
              />
            </Modal.Body>
            <Modal.Footer>
              {isImageUpdateLoading ? (
                <>
                  <ButtonComponentV2
                    titel="Saving..."
                    icon={<FontAwesomeIcon icon={faSpinner} spin />}
                    isDisabled={isSaveButtonDisabled || isImageUpdateLoading}
                    color="#fff"
                    backgroundColor="#590B21"
                  />
                </>
              ) : (
                <ButtonComponentV2
                  titel="Save"
                  isDisabled={isSaveButtonDisabled || isImageUpdateLoading}
                  onClick={handleCartPersonImageUpdate}
                  color="#fff"
                  backgroundColor="#590B21"
                />
              )}
            </Modal.Footer>
          </Modal>
        </>
      )}
    </>
  );
};

export default CartPersonDetails;